'use client';

import { useState } from 'react';
import {
	ArrowDownToLine,
	ArrowUpFromLine,
	Sigma,
	Sparkles,
	ChevronDown,
	ChevronRight,
	Cpu,
	Database
} from 'lucide-react';
import { TokenUsage } from '@/types';

interface TokenUsageDisplayProps {
	tokenUsage: TokenUsage;
	className?: string;
}

/**
 * Compact display of AI token usage metrics
 * Shows input tokens, output tokens, cache tokens, and total with icons
 * Expandable breakdown by step when available
 */
export default function TokenUsageDisplay({ tokenUsage, className = '' }: TokenUsageDisplayProps) {
	const { inputTokens, outputTokens, cacheTokens, totalTokens, breakdown, model } = tokenUsage;
	const [isExpanded, setIsExpanded] = useState(false);

	// Don't render if all values are 0
	if (inputTokens === 0 && outputTokens === 0 && totalTokens === 0) {
		return null;
	}

	// Default model for old messages that don't have model stored
	const DEFAULT_MODEL = 'gemini-3-flash-preview';
	const displayModel = model || DEFAULT_MODEL;

	const hasBreakdown = breakdown && breakdown.length > 0;
	const hasCacheTokens = cacheTokens > 0;

	return (
		<div className={`text-xs ${className}`}>
			{/* Summary row */}
			<div className='flex flex-wrap items-center gap-x-3 gap-y-1'>
				{/* Label with expand toggle if breakdown exists */}
				<button
					type='button'
					onClick={() => hasBreakdown && setIsExpanded(!isExpanded)}
					className={`text-muted-foreground flex items-center gap-1.5 ${hasBreakdown ? 'hover:text-foreground cursor-pointer' : 'cursor-default'}`}
					disabled={!hasBreakdown}
				>
					{hasBreakdown && (isExpanded ? <ChevronDown className='h-3 w-3' /> : <ChevronRight className='h-3 w-3' />)}
					<Sparkles className='h-3 w-3' />
					<span className='font-medium'>Tokens</span>
				</button>

				{/* Divider */}
				<div className='bg-border h-3 w-px' />

				{/* Token metrics */}
				<div className='flex items-center gap-3'>
					<div
						className='group text-muted-foreground flex cursor-default items-center gap-1 transition-colors hover:text-emerald-400'
						title='Input tokens (prompt)'
					>
						<ArrowDownToLine className='h-3 w-3 transition-transform group-hover:scale-110' />
						<span>{inputTokens.toLocaleString()}</span>
						<span className='text-muted-foreground hidden text-[10px] group-hover:inline'>in</span>
					</div>
					<div
						className='group text-muted-foreground flex cursor-default items-center gap-1 transition-colors hover:text-blue-400'
						title='Output tokens (response)'
					>
						<ArrowUpFromLine className='h-3 w-3 transition-transform group-hover:scale-110' />
						<span>{outputTokens.toLocaleString()}</span>
						<span className='text-muted-foreground hidden text-[10px] group-hover:inline'>out</span>
					</div>
					{hasCacheTokens && (
						<div
							className='group text-muted-foreground flex cursor-default items-center gap-1 transition-colors hover:text-amber-400'
							title='Cache tokens (cached context reuse)'
						>
							<Database className='h-3 w-3 transition-transform group-hover:scale-110' />
							<span>{cacheTokens.toLocaleString()}</span>
							<span className='text-muted-foreground hidden text-[10px] group-hover:inline'>cache</span>
						</div>
					)}
					<div
						className='group text-foreground flex cursor-default items-center gap-1 font-medium transition-colors'
						title='Total tokens used (input + output + cache)'
					>
						<Sigma className='h-3 w-3 transition-transform group-hover:scale-110' />
						<span>{totalTokens.toLocaleString()}</span>
						<span className='text-muted-foreground hidden text-[10px] group-hover:inline'>total</span>
					</div>
				</div>

				{/* Model indicator */}
				<div className='bg-border h-3 w-px' />
				<div
					className='group text-muted-foreground flex cursor-default items-center gap-1 whitespace-nowrap transition-colors hover:text-purple-400'
					title={`AI Model: ${displayModel}`}
				>
					<Cpu className='h-3 w-3 flex-shrink-0 transition-transform group-hover:scale-110' />
					<span className='truncate'>{displayModel}</span>
				</div>
			</div>

			{/* Breakdown section */}
			{hasBreakdown && isExpanded && (
				<div className='border-border mt-2 ml-4 space-y-1 border-l pl-3'>
					{breakdown.map((step, index) => {
						const stepHasCache = step.cacheTokens > 0;
						return (
							<div
								key={`${step.step}-${index}`}
								className='text-muted-foreground hover:text-muted-foreground flex items-center justify-between'
							>
								<span
									className='max-w-[140px] truncate'
									title={step.label}
								>
									{step.label}
								</span>
								<div className='flex items-center gap-2 tabular-nums'>
									<span className='text-emerald-500/70'>{step.inputTokens.toLocaleString()}</span>
									<span className='text-muted-foreground'>/</span>
									<span className='text-blue-500/70'>{step.outputTokens.toLocaleString()}</span>
									{stepHasCache && (
										<>
											<span className='text-muted-foreground'>/</span>
											<span className='text-amber-500/70'>{step.cacheTokens.toLocaleString()}</span>
										</>
									)}
									<span className='text-muted-foreground'>=</span>
									<span className='text-muted-foreground'>{step.totalTokens.toLocaleString()}</span>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}
